import { createApp } from 'vue';
import { assign, has, upperFirst, toLower } from 'lodash';
import { ConfigurationService, registry } from '@nsftx/games-config';
import * as packageTranslations from '@nsftx/i18n-games-racers';
import createTranslationsProxy from '@nsftx/games-sdk-js/src/utility/translationsProxy';
import eventBus from '@nsftx/games-sdk-js/src/utility/eventBus';
import sentry from '@nsftx/games-sdk-js/src/utility/sentry';
import gamesStatic from '@nsftx/games-sdk-js/src/api/gamesStatic';
import webViewIntegration from '@/utility/webViewIntegration';
import App from './App';
import router from './router';
import store from './store';

const qp = new URLSearchParams(window.location.search);
// TODO Update definitions of all other query params to make it more readable
let productName = qp.get('productName');
const messageProtocol = qp.get('messageProtocol') || qp.get('integrationType') || 'postMessage';
const tenantId = qp.get('tenantId');
/**
 * Used only to display correct layout and style, without considering any logic.
 * You should use only this prop for layout and style differences between web and terminal.
 *
 * Possible values:
 * - `"terminal"`: Renders the terminal-style layout.
 * - `"web"`: Renders the standard web layout.
 *
 * Default value: `"web"`
 * @type {"terminal" | "web"}
 */
const allowedLayouts = ['web', 'terminal'];
const queryLayout = qp.get('layout');
const layout = allowedLayouts.includes(queryLayout) ? queryLayout : 'web';
const appName = qp.get('deliveryPlatform') || qp.get('application') || 'web';
const token = qp.get('token');
const clientType = qp.get('authStrategy') || qp.get('clientType');
const playerId = qp.get('id') || qp.get('playerId') || qp.get('player');
const appMode = qp.get('appMode');
const appDeployment = window.location.hostname.includes('nsoft.app') ? 'cf' : 'netlify';
const platform = qp.get('platform');
const customDomain = qp.get('customDomain');
const isNextGen = platform === 'NextGen';
const parent = qp.get('parent');
const nextGenStandaloneIntegration = typeof parent !== 'undefined' && parent === 'false';
const productNamePrefixes = ['SRMV', 'NVNV', 'NSNV'];
for (const productPrefix of productNamePrefixes) {
  if (productName.startsWith(productPrefix)) productName = productName.slice(productPrefix.length);
}
let deviceData;
let betshopData;
let companyData;
let accountData;
let productUuid = null;

const checkLocale = (locale) => {
  if (locale === 'sr') {
    return 'sr-Latn';
  }
  // In the future, additional locale cases might be required
  return locale;
};
const getRacersColors = (product) => {
  const racerLabelColors = {
    GreyhoundRaces: ['bf3030', '3498db', 'ffffff', '000000', 'd35400', ['000000', '#fff'], '779C08', 'C9AB0A'],
    VirtualMotorcycleSpeedway: ['bf3030', '3498db', 'ffffff', 'ebc40f'],
    VirtualHorseRaces: ['30BF78', '5E411C', 'EBC40F', 'BF3030', '3498DB', '762E95', 'FFFFFF', '000000'],
    VirtualGreyhoundRaces: ['bf3030', '3498db', 'ffffff', '000000', 'd35400', ['000000', '#fff'], '779C08', 'C9AB0A'],
  };
  return racerLabelColors[product];
};

const getProductNameLabel = (product) => {
  const keyType = store.state.usePackageTranslations ? 'package' : 'standard';
  const productNames = {
    package: {
      GreyhoundRaces: 'product_name_gr',
      VirtualGreyhoundRaces: 'product_name_vgr',
      VirtualHorseRaces: 'product_name_vhr',
      VirtualMotorcycleSpeedway: 'product_name_vms',
    },
    standard: {
      GreyhoundRaces: 'pages_player_history_greyhound',
      VirtualGreyhoundRaces: 'pages_player_history_virtual_greyhound',
      VirtualHorseRaces: 'pages_player_history_virtual_horse',
      VirtualMotorcycleSpeedway: 'pages_player_history_virtual_motorcycle_speedway',
    },
  };
  const productNameLabels = {
    GreyhoundRaces: productNames[keyType].GreyhoundRaces,
    VirtualGreyhoundRaces: productNames[keyType].VirtualGreyhoundRaces,
    VirtualHorseRaces: productNames[keyType].VirtualHorseRaces,
    VirtualMotorcycleSpeedway: productNames[keyType].VirtualMotorcycleSpeedway,
  };
  return productNameLabels[product];
};
window.addEventListener('message', (event) => {
  // Slave.Load message can occur before bus init
  if (event.data.action === 'Slave.Load') {
    productUuid = event.data.data.account.uuid;
  }
});

if (isNextGen) {
  window.addEventListener('message', (event) => {
    if (event.data.action === 'User.AuthorizationChanged') {
      const authData = event.data.data.auth;
      const loggedIn = !!authData.token;
      authData.user.logged = loggedIn;
      store.dispatch('setUserBalance', authData.user.balance);
      store.dispatch('user/updateUser', {
        auth: authData,
        profile: authData.user,
      });
    }
    if (event.data.action === 'User.BalanceChanged') {
      store.dispatch('setUserBalance', event.data.data.balance);
    }
  });

  window.addEventListener('Configuration:SetConfiguration', (event) => {
    store.dispatch('setNextGenConfig', event.detail);
  });
}

window.addEventListener('online', () => {
  store.dispatch('updateTicketValues');
  eventBus.$emit('updateSchedule');
});

window.addEventListener('visibilitychange', () => {
  store.dispatch('updateTicketValues');
  eventBus.$emit('updateSchedule');
});

if (appName === 'terminal') {
  store.dispatch('user/setTerminalUser', true);
  window.addEventListener('message', (event) => {
    if (event.data.action === 'Slave.Load') {
      const { device, betshop, company, account, thirdPartyInfo } = event.data.data;
      if (event.data?.data?.settings) {
        store.dispatch('setTerminalMouseCursorEnabled', event.data.data.settings.showCursor);
      }
      deviceData = device;
      betshopData = betshop;
      companyData = company;
      accountData = account;
      store.dispatch('setUserBalance', event.data.data.terminal.balance);
      store.dispatch('setTerminalUserData', event.data.data.user);
      store.dispatch('setThirdPartyInfo', thirdPartyInfo);
      if (event.data.data.betslip.blockers.length) {
        store.dispatch('disableAllBetting', true);
        store.dispatch('gamesBetslip/setBetslipBlockers', {
          blockers: event.data.data.betslip.blockers,
          type: 'add',
        });
      }
    }
    if (event.data.action === 'Betslip.Blocked') {
      store.dispatch('disableAllBetting', true);
      store.dispatch('gamesBetslip/setBetslipBlockers', {
        blockers: event.data.data.blockers,
        type: 'add',
      });
    }
    if (event.data.action === 'Betslip.Unblocked') {
      store.dispatch('disableAllBetting', false);
      store.dispatch('gamesBetslip/setBetslipBlockers', {
        blockers: event.data.data.blockers,
        type: 'remove',
      });
    }
    if (has(event.data.data, 'code') && event.data.data.code === 'T_PAYIN_VALIDATE_INSUFFICIENT_FUNDS') {
      store.dispatch('gamesBetslip/setIsPayinInProgress', false);
      store.dispatch('setScheduleDisabled', false);
      store.dispatch('stopBetting', 0);
      store.dispatch('gamesBetslip/setIsPayinButtonDisabled', false);
    }
    if (has(event.data.data, 'code') && event.data.data.code === 'T_PASS_CARDS_TERMINAL_LOCKED') {
      store.dispatch('gamesBetslip/setIsPayinInProgress', false);
      store.dispatch('gamesBetslip/setIsPayinButtonDisabled', false);
      store.dispatch('setScheduleDisabled', false);
      store.dispatch('stopBetting', 0);
    }
    if (
      (has(event.data.data, 'code') && event.data.data.code === 'error') ||
      (has(event.data.data, 'code') && event.data.data.code === 'UNKNOWN') ||
      (has(event.data.data, 'code') && event.data.data.code === 'T_PAYIN_VALIDATION_PRINT_STATUS_ERR')
    ) {
      store.dispatch('disable_betting', false);
      store.dispatch('gamesBetslip/setIsPayinButtonDisabled', false);
      store.dispatch('gamesBetslip/setIsPayinInProgress', false);
    }
  });
}

const getSlaveId = (id) => {
  if (id === '/ticket-history') {
    return `Plugin${productName}TicketHistory`;
  }
  if (id === '/results') {
    return `Plugin${productName}Results`;
  }
  if (id === '/ticket-details') {
    return `Plugin${productName}TicketDetails`;
  }
  return `Plugin${productName}`;
};
const clientAdapters = [];
const nextGenAdapterData = {
  name: 'IgniteAdapter',
  options: {
    allowedOrigins: '*',
    displayId: productName.toLowerCase(),
    version: '1.1',
    providerId: process.env.VUE_APP_ENVIRONMENT === 'production' ? 30 : 26,
    environment: process.env.VUE_APP_ENVIRONMENT,
  },
};
const gravityGatewayAdapterData = {
  name: 'GravityGatewayAdapter',
  options: {
    slaveId: getSlaveId(window.location.pathname),
    eventPropagation: {
      click: true,
      keydown: '*',
    },
  },
};
if (isNextGen) {
  clientAdapters.push(nextGenAdapterData);
} else {
  clientAdapters.push(gravityGatewayAdapterData);
}
(async () => {
  const configSettings = {
    environment: process.env.VUE_APP_ENVIRONMENT,
    requiredPaths: [],
    messageProtocol,
    applicationName: upperFirst(appName),
    productName,
    channel: appName,
    clientAdapters,
    ui: tenantId,
    deployment: appDeployment,
    customDomain,
  };
  const usePackageTranslations = store.state.usePackageTranslations;
  if (!usePackageTranslations) configSettings.translationDomains = ['web', 'glossary'];
  const configService = new ConfigurationService(configSettings);
  const config = await configService.getConfiguration();
  const product = registry.getProduct(config.productName);
  config.customDomain = customDomain;
  config.locale = checkLocale(config.locale);
  const [alias] = product.aliases;

  if (playerId) {
    store.state.config.playerId = playerId;
  }

  store.state.config.racerLabelColors = getRacersColors(productName);
  store.state.config.productNameLabel = getProductNameLabel(productName);
  store.state.config.productAlias = alias;
  store.state.config.reverseForecast = config.bets[12].value;
  store.state.visualizationUrl =
    appDeployment === 'cf'
      ? process.env.VUE_APP_VISUALIZATION_BASEURL_CF
      : process.env.VUE_APP_VISUALIZATION_BASEURL_NETLIFY;
  store.state.config.rules = config.rules;
  store.state.config.taxes = config.taxes;
  store.state.config.productUuid = productUuid;
  if (appName === 'terminal') {
    store.state.config.device = deviceData;
    store.state.config.betshop = betshopData;
    store.state.config.companyData = companyData;
    store.state.config.account = accountData;
  }
  store.dispatch('setDeviceLayout');
  store.dispatch('setConfig', config);
  if (isNextGen) {
    await store.dispatch('updateConfigWithNextGenConfig');
    store.dispatch('setNextGenStandaloneIntegration', nextGenStandaloneIntegration);
  }
  store.dispatch('setRules', config.rules);
  store.dispatch('setLayout', layout.toLowerCase());
  store.dispatch('setAppMode', appMode);
  if (usePackageTranslations) {
    // NPM PACKED TRANS
    const translations = await packageTranslations.exportTranslations(config.locale, {
      companyName: config.tenantName || config.rawData?.companyInfo?.name,
      companyUuid: tenantId,
      env: process.env.NODE_ENV,
    });
    const proxyTranslations = createTranslationsProxy(translations, ['greyhound_', 'vms_']);
    await store.dispatch('setTranslations', proxyTranslations);
  } else {
    // STANDARD STATIC + HTTP CONFIG TRANS
    const translations = await gamesStatic.getTranslations(config.locale);
    await store.dispatch('setTranslations', assign(config.translations, translations));
  }
  const paymentStrategy = {
    paymentStrategy: config.paymentStrategy,
  };
  const isPaymentPerBetStrategy = config.paymentStrategy[toLower(appName)].value === 'perBet';
  const betslipStaticConfig = config.ui?.config?.betslip;
  const mergedRules = { ...config.rules, ...paymentStrategy };
  const paymentChips = config.ui?.config?.paymentChips;
  const betslipConfig = {
    betLayout: betslipStaticConfig?.betLayout ?? 'A',
    channel: config.applicationName,
    ticketTypes: betslipStaticConfig?.ticketTypes ?? ['single'],
    rules: mergedRules,
    taxes: config.taxes,
    isPossibleWinActive: betslipStaticConfig?.isPossibleWinActive ?? false,
    isPaymentBetAllowed: betslipStaticConfig?.isPaymentBetAllowed ?? true,
    isPaymentPerBetAllowed: betslipStaticConfig?.isPaymentPerBetAllowed ?? true,
    isPaymentPerBetStrategy,
    isFuturePerBetAllowed: false,
    isFuturePerTicketAllowed: false,
    isTicketCancelAllowed: config.cancelEnabled ?? true,
    isTicketRebetAllowed: false,
    isAddButtonActive: false,
    minIncrement: betslipStaticConfig?.minIncrement ?? 0.01,
    futureRounds: 1,
    paymentChips,
  };
  await store.dispatch('setRules', mergedRules);
  await store.dispatch('gamesBetslip/setConfig', betslipConfig);

  if (token) {
    if (config.ui.config.mode === 'standalone') {
      store.state.config.platformConfig.clientType = clientType;
      store.state.config.platformConfig.playerId = playerId;
      await store.dispatch('getPlayer', { token });
    }
    await store.dispatch('user/setUserSessionToken', token);
  }

  const app = createApp({
    extends: App,
    beforeCreate() {
      if (!webViewIntegration.isActive() || !window.WebAppListener) return;
      window.WebAppListener.sendMessage('Slave.Init');
    },
  });

  if (process.env.VUE_APP_SENTRY_ACTIVE === 'true') {
    sentry.start({
      app,
      dsn: process.env.VUE_APP_SENTRY_DSN,
      sampleRate: 0.01,
      trackComponents: true,
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'Network Error',
        'timeout of 5000ms exceeded',
        'timeout of 10000ms exceeded',
        'Android is not defined',
        /.*NotAllowedError.*/gm,
        /.*WakeLock.*/gm,
      ],
    });
  }

  app.config.productionTip = false;
  app.use(router).use(store).mount('#app');
})();
